import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { clientsApi } from '@/lib/api'
import type { Client, ClientFormData } from '@/types'

export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: () => [...clientKeys.lists()] as const,
  detail: (id: number) => [...clientKeys.all, 'detail', id] as const,
}

export function useClients(): UseQueryResult<Client[]> {
  return useQuery({
    queryKey: clientKeys.list(),
    queryFn: async () => {
      const res = await clientsApi.list()
      return res.data
    },
  })
}

export function useClient(id: number): UseQueryResult<Client> {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: async () => {
      const res = await clientsApi.get(id)
      return res.data
    },
    enabled: Boolean(id),
  })
}

export function useCreateClient(): UseMutationResult<
  Client,
  Error,
  ClientFormData
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: ClientFormData) => {
      const res = await clientsApi.create(data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}

export function useUpdateClient(): UseMutationResult<
  Client,
  Error,
  { id: number; data: ClientFormData }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await clientsApi.update(id, data)
      return res.data
    },
    onSuccess: (updatedClient) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
      queryClient.setQueryData(clientKeys.detail(updatedClient.id), updatedClient)
    },
  })
}

export function useDeleteClient(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await clientsApi.delete(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}
